/**
 * セクション AI 最適化ダイアログ。
 * 指示文 + 任意の参照 URL + 任意の参照 PDF を受け取り、
 * Firebase Callable `optimizeDocumentSection` を呼び出して
 * 対象セクションの config を書き換える。
 */
import { FileText, Loader2, Sparkles, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useOptimizeDocumentSection } from '@/hooks/useDocuments';

const MAX_PDF_BYTES = 8 * 1024 * 1024;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  sectionId: string;
  sectionLabel: string;
  onOptimized?: (config: Record<string, unknown>) => void;
}

export function DocumentSectionOptimizeDialog({
  open,
  onOpenChange,
  documentId,
  sectionId,
  sectionLabel,
  onOptimized,
}: Props) {
  const [prompt, setPrompt] = useState('');
  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState('');
  const [pdfSize, setPdfSize] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const optimize = useOptimizeDocumentSection();

  const reset = () => {
    setPrompt('');
    setUrl('');
    setKeywords('');
    setPdfBase64(null);
    setPdfName('');
    setPdfSize(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickPdf = async (file: File | null | undefined) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      toast.error('PDF ファイル (.pdf) を選択してください');
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      toast.error(
        `PDF は ${Math.floor(MAX_PDF_BYTES / 1024 / 1024)}MB 以下にしてください`,
      );
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setPdfBase64(base64);
      setPdfName(file.name);
      setPdfSize(file.size);
    } catch (e) {
      toast.error(`PDF 読み込み失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleClearPdf = () => {
    setPdfBase64(null);
    setPdfName('');
    setPdfSize(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOptimize = async () => {
    if (!prompt.trim()) {
      toast.error('最適化の指示文を入力してください');
      return;
    }
    if (url.trim() && !/^https?:\/\//i.test(url.trim())) {
      toast.error('URL は http:// または https:// で始めてください');
      return;
    }
    try {
      const res = await optimize.mutateAsync({
        documentId,
        sectionId,
        prompt: prompt.trim(),
        referenceUrl: url.trim() || undefined,
        referenceKeywords: keywords.trim() || undefined,
        pdfBase64: pdfBase64 || undefined,
        pdfName: pdfBase64 ? pdfName : undefined,
      });
      toast.success('セクションを最適化しました');
      onOptimized?.(res.config);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(`最適化失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && optimize.isPending) return; // 処理中は閉じさせない
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-[var(--accent)]" />
            セクションを AI で最適化
          </DialogTitle>
          <DialogDescription>
            「{sectionLabel}」の内容を、下記の指示と参照情報に基づいて書き換えます。
            画像は維持されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-xs">
          <div className="space-y-1.5">
            <Label htmlFor="opt-prompt">最適化の指示 (必須)</Label>
            <Textarea
              id="opt-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder={
                '例: もっと専門用語を減らして、初めて聞く方にも伝わるように。\n例: 強み 3 点をより具体的な数字で強化。\n例: トーンをもう少しカジュアルに。'
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="opt-url">参照 URL (任意)</Label>
            <Input
              id="opt-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/spec"
              className="font-mono"
            />
            <p className="text-[10px] text-[var(--text-muted)]">
              指定すると、サーバが該当ページの本文を取得し参考にします。
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="opt-keywords">補足キーワード (任意)</Label>
            <Input
              id="opt-keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="例: 価格, 導入事例, 仕様"
            />
            <p className="text-[10px] text-[var(--text-muted)]">
              カンマ区切り。URL / PDF からこのキーワードを含む段落を優先抽出します。
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="opt-pdf">参照 PDF (任意)</Label>
            {pdfBase64 ? (
              <div className="flex items-center gap-2 p-2 border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]">
                <FileText className="size-4 text-[var(--accent)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] truncate" title={pdfName}>
                    {pdfName}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {(pdfSize / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearPdf}
                  className="text-[var(--text-muted)] hover:text-red-500"
                  title="PDF を解除"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  id="opt-pdf"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => void handlePickPdf(e.target.files?.[0])}
                  className="block w-full text-[11px] file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border file:border-[var(--border)] file:bg-[var(--bg-elevated)] file:text-[11px] file:cursor-pointer hover:file:bg-[var(--bg-hover)]"
                />
                <p className="text-[10px] text-[var(--text-muted)]">
                  最大 {Math.floor(MAX_PDF_BYTES / 1024 / 1024)}MB。サーバが PDF からテキスト抽出して参照します。
                </p>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={optimize.isPending}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleOptimize}
            disabled={optimize.isPending || !prompt.trim()}
          >
            {optimize.isPending ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                最適化中...
              </>
            ) : (
              <>
                <Sparkles className="size-3.5 mr-1" />
                最適化を実行
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('読み込み結果が文字列ではありません'));
        return;
      }
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('読み込み失敗'));
    reader.readAsDataURL(file);
  });
}
